import { SimpleValue } from '@SonarCX/pages/Customers/FieldFilters/FieldPills'

import { arrayToQuery, arrayToQueryEncode } from '../../lib/api/helpers'
import { embedsToQuery } from '../../lib/api/helpers'
import { DEFAULT_EMBEDS } from '../../providers/UserAPIProvider'

export type ContactsTableFiltersOwner = {
  ownerId: string
  ownerOption: string
}

export type ContactsTableFilters = {
  owner: ContactsTableFiltersOwner
  type: string
  status: string
}

export type AlternatePagination = Omit<IndexPagination, 'total'>

export type CustomFieldSearch = {
  customFields: LocalCustomField[]
  fields: string[]
  values: SimpleValue[]
  includedCustom: string[]
}

export const FIELD_LIMIT = 5
export const FIELD_INCLUDE_LIMIT = 5

export const getUrlBySortAndFilters = (
  filters: ContactsTableFilters,
  sort: Array<'' | string>
) => {
  let url = ''
  if (sort.length > 0) {
    url += `&${arrayToQueryEncode('sort', sort)}`
  }
  if (!filters) return url
  if (filters.owner.ownerId !== 'null') {
    url += `&owner_id=${filters.owner.ownerId}`
  }
  if (filters.type !== 'null') {
    url += `&type_id=${filters.type}`
  }
  if (filters.status !== 'null') {
    url += `&status_id=${filters.status}`
  }

  return url
}

export const getUrl = (
  filters: ContactsTableFilters,
  sort: Array<'' | string>,
  pagination: AlternatePagination,
  customFieldSearch: CustomFieldSearch = {
    customFields: [],
    fields: [],
    values: [],
    includedCustom: []
  }
) => {
  let keywordQuery = ''
  if (customFieldSearch.fields.length > 0) {
    const allKeywords = `${getFieldsKeyWords(customFieldSearch)}`
    keywordQuery = `&keywords=${encodeURIComponent(
      allKeywords.trim().toLocaleLowerCase().padEnd(3).slice(0, 25)
    )}`
  }

  let fieldsQuery = ''
  if (customFieldSearch.fields.length > 0) {
    fieldsQuery = `&${arrayToQuery(
      'filter_custom',
      customFieldSearch.fields.slice(0, FIELD_LIMIT)
    )}`
  }

  let includedFieldsQuery = ''
  if (customFieldSearch.includedCustom.length > 0) {
    includedFieldsQuery = `&${arrayToQuery(
      'include_custom',
      customFieldSearch.includedCustom
    )}`
  }

  let url = `/users${embedsToQuery(
    DEFAULT_EMBEDS
  )}${keywordQuery}${fieldsQuery}${includedFieldsQuery}&external=${true}&page=${
    pagination.page
  }&per_page=${pagination.per_page}${getUrlBySortAndFilters(filters, sort)}`

  return url
}

export const getFieldsKeyWords = (
  info: CustomFieldSearch,
  limit = FIELD_LIMIT
) => {
  const { customFields, fields, values } = info

  const trimmedFields = fields.slice(0, limit)
  if (trimmedFields.length === 0 || customFields.length === 0) {
    return ''
  }

  const getValue = (field: LocalCustomField, val: any) => {
    if (!field || !val) {
      return ''
    }

    if (['date-only', 'date-time', 'month-year'].includes(field.field_type)) {
      try {
        let valToSend = ''

        if (typeof val === 'string') {
          valToSend = val
        } else {
          valToSend = (val as Date).toISOString()
        }

        // Don't check time part for day only fields
        if (field.field_type === 'date-only') {
          return valToSend.split('T')[0]
        }

        // Only match on year and day
        if (field.field_type === 'month-year') {
          const firstPart = valToSend.split('T')[0]
          const parts = firstPart.split('-')
          return `${parts[0]}-${parts[1]}`
        }

        // Exact match for day and time
        return valToSend
      } catch {
        return ''
      }
    }

    return val as string
  }

  const getDefaultValue = (field: LocalCustomField) => {
    if (!field) {
      return ''
    }

    // Only field type that has a valid default
    if (field.field_type === 'boolean') {
      return 'false'
    }

    return ''
  }

  const fixedValues: Record<string, string> = {}

  trimmedFields.forEach(id => {
    const field = customFields.find(field => field.id === id)
    fixedValues[id] = getValue(field, values[id]) || getDefaultValue(field)
  })

  const validValues = Object.values(fixedValues).filter(Boolean)

  return validValues.join(' ')
}

export const parseSort = item => {
  // Maps table accessor to api sort id
  const idMap = {
    name: 'name',
    status_id: 'status',
    type_id: 'type',
    updated_at: 'updated',
    created_at: 'created'
  }

  const { id, desc } = item

  if (!(id in idMap)) {
    return null
  }

  return `${desc ? '-' : '+'}${idMap[id]}`
}
